import React, { useMemo, useState } from "react";
import { Box, Button, MenuItem, Select } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "../SubTitle.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, format, isValid, subDays } from "date-fns";
import {
  formatDate,
  getUserDateFormat,
  convertToDateFnsFormat,
} from "../../../utils/formatDate";
import {
  getInclusiveDateRangeDays,
  MAX_ANALYTICS_DATE_RANGE_DAYS,
} from "../../../utils/analyticsDateRange";

export default function SubTitleNew({
  title,
  path = "",
  add,
  addPath,
  track,
  trackPath,
  track2,
  onAddClick,
  onTrackClick,
  onTrack2Click,
  dateLabel = "Today",
  dateOptions,
  dateError = "",
  onDateRangeChange,
  showDashboardControls = true,
  onDateClick,
  onCustomizeClick,
  breadcrumbHandlers = {},
  type = null,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [selectedDateLabel, setSelectedDateLabel] = useState(dateLabel);
  const [selectedDateValue, setSelectedDateValue] = useState(
    String(dateLabel || "Today")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_"),
  );
  const [customRange, setCustomRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [customRangeError, setCustomRangeError] = useState("");

  const userDateFormat = getUserDateFormat();
  const dateInputFormat = convertToDateFnsFormat(userDateFormat);
  const parseDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return isValid(d) ? d : null;
    const parsed = new Date(`${d}T00:00:00`);
    return isValid(parsed) ? parsed : null;
  };

  const maxCustomSpanDays = MAX_ANALYTICS_DATE_RANGE_DAYS - 1;
  const customStartDate = parseDate(customRange.startDate);
  const customEndDate = parseDate(customRange.endDate);

  const getCustomRangeError = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return "Please select both start and end dates";
    }

    const start = parseDate(startDate);
    const end = parseDate(endDate);
    if (!start || !end) {
      return "Please select a valid date range";
    }

    if (start > end) {
      return "Start date must be before or equal to end date";
    }

    if (getInclusiveDateRangeDays(startDate, endDate) > MAX_ANALYTICS_DATE_RANGE_DAYS) {
      return "Date range cannot exceed 30 days";
    }

    return "";
  };

  const presets = useMemo(
    () =>
      dateOptions || [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last 7 Days", value: "last_7_days" },
        { label: "Last 30 Days", value: "last_30_days" },
        { label: "This Week", value: "this_week" },
        { label: "This Month", value: "this_month" },
      ],
    [dateOptions],
  );

  const getBreadcrumbNavigationTarget = (pathSegment, index, segments) => {
    const trimmedSegment = pathSegment.trim();

    // First segment (organization) should always go to dashboard
    if (index === 0) {
      return { to: "/dashboard" };
    }

    // Don't navigate for the last segment (current page)
    if (index === segments.length - 1) {
      return null;
    }

    const customHandler = breadcrumbHandlers[trimmedSegment];
    if (customHandler) {
      if (typeof customHandler === "string") {
        return { to: customHandler };
      }

      if (typeof customHandler === "object" && customHandler.to) {
        return customHandler;
      }
    }

    const pathMap = {
      Routes: "/route",
      Vehicles: "/vehicle",
      Stops: "/stop",
      Users: "/user",
      Schedules: "/schedule",
      Reports: "/reports",
      Sites: "/site",
      "Manage Roles": "/user/role",
      "Data Management": "/reports/managedata",
      "Trip Replays": "/replay",
      Organizations: "/org",
      "New Request": "/admin",
      Devices: "/device",
    };

    const mappedPath = pathMap[trimmedSegment];
    return mappedPath ? { to: mappedPath } : null;
  };

  // Function to handle breadcrumb navigation
  const handleBreadcrumbClick = (pathSegment, index, segments) => {
    const target = getBreadcrumbNavigationTarget(pathSegment, index, segments);
    if (!target) {
      return;
    }

    navigate(target.to, {
      state: target.state,
      replace: target.replace,
    });
  };

  // Function to render clickable breadcrumbs
  const renderBreadcrumbs = (path) => {
    const segments = path.split(">");

    return segments.map((segment, index) => {
      const isClickable = Boolean(
        getBreadcrumbNavigationTarget(segment, index, segments),
      );

      return (
        <React.Fragment key={index}>
          <span
            className={`subtitle-breadcrumb ${isClickable ? "clickable" : ""}`}
            onClick={() => handleBreadcrumbClick(segment, index, segments)}
          >
            {t(segment.trim())}
          </span>
          {index < segments.length - 1 && (
            <span className="subtitle-separator"> &gt; </span>
          )}
        </React.Fragment>
      );
    });
  };

  const handleAddClick = () => {
    if (onAddClick) {
      onAddClick(addPath);
    }
  };

  const handleTrackClick = () => {
    if (onTrackClick) {
      onTrackClick(trackPath);
      return;
    }

    if (trackPath) {
      navigate(trackPath);
    }
  };

  const handlePresetSelect = (preset) => {
    const presetValue = preset.value || preset.label;
    setSelectedDateLabel(preset.label);
    setSelectedDateValue(presetValue);
    if (String(presetValue).toLowerCase() === "custom") {
      setShowCustomRange(true);
      return;
    }
    setShowCustomRange(false);
    onDateRangeChange?.({ type: "preset", ...preset });
  };

  const handlePresetChange = (event) => {
    const value = event.target.value;
    const preset = presets.find((item) => (item.value || item.label) === value);
    if (!preset) return;
    onDateClick?.();
    handlePresetSelect(preset);
  };

  const handleCustomizeClick = () => {
    setShowCustomRange((current) => !current);
    onCustomizeClick?.();
  };

  const updateCustomRange = (field, value) => {
    setCustomRangeError("");
    setCustomRange((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const applyCustomRange = () => {
    const rangeError = getCustomRangeError(
      customRange.startDate,
      customRange.endDate,
    );
    if (rangeError) {
      setCustomRangeError(rangeError);
      return;
    }

    setCustomRangeError("");
    const formattedRange = `${formatDate(customRange.startDate)} - ${formatDate(customRange.endDate)}`;
    setSelectedDateLabel(formattedRange);
    setSelectedDateValue("custom");
    setShowCustomRange(false);
    onDateRangeChange?.({ type: "custom", ...customRange });
  };

  return (
    <div className="subtitle-new-container">
      <div className="subtitle-new-left-content">
        <Box>
          <span className="subtitle-new-text">{t(title)}</span>
          <div className="subtitle-new-path">{renderBreadcrumbs(path)}</div>
        </Box>
      </div>

      <div className="subtitle-new-actions">
        <div className="subtitle-new-action-row">
          {track2 && (
            <button
              type="button"
              className="subtitle-new-secondary-btn"
              onClick={onTrack2Click}
            >
              {t(track2)}
            </button>
          )}
          {track && (
            <button
              type="button"
              className="subtitle-new-secondary-btn"
              onClick={handleTrackClick}
            >
              {t(track)}
            </button>
          )}

          {add && (
            <button
              type="button"
              className="subtitle-new-primary-btn"
              onClick={handleAddClick}
            >
              <AddIcon sx={{ fontSize: 16 }} />
              <span>
                {String(t(String(add).replace(/^\s*\+\s*/g, "").trim() || add) || add)
                  .replace(/^\s*\+\s*/g, "")
                  .trim()}
              </span>
            </button>
          )}
        </div>

        {showDashboardControls && (
          <div className="subtitle-new-tools-row">
            <div
              className="subtitle-new-date-btn"
              aria-label={t("Select Date Range")}
            >
              <Select
                value={selectedDateValue}
                onChange={handlePresetChange}
                displayEmpty
                variant="standard"
                disableUnderline
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{
                  PaperProps: { className: "subtitle-new-date-menu-paper" },
                  MenuListProps: { className: "subtitle-new-date-menu-list" },
                }}
                renderValue={() => {
                  const displayLabel =
                    selectedDateValue === "custom" &&
                    customRange.startDate &&
                    customRange.endDate
                      ? `${formatDate(customRange.startDate)} - ${formatDate(customRange.endDate)}`
                      : t(selectedDateLabel);
                  return (
                    <span className="subtitle-new-date-value">
                      <CalendarTodayIcon sx={{ fontSize: 16 }} />
                      <span>{displayLabel}</span>
                    </span>
                  );
                }}
              >
                {presets.map((preset) => (
                  <MenuItem
                    className="subtitle-new-date-menu-item"
                    key={preset.value || preset.label}
                    value={preset.value || preset.label}
                  >
                    {t(preset.label)}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <button
              type="button"
              className="subtitle-new-customize-btn"
              onClick={handleCustomizeClick}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.99935 4.00033V2.66699M7.99935 4.00033C7.26346 4.00033 6.66602 4.59777 6.66602 5.33366C6.66602 6.06955 7.26346 6.66699 7.99935 6.66699M7.99935 4.00033C8.73524 4.00033 9.33268 4.59777 9.33268 5.33366C9.33268 6.06955 8.73524 6.66699 7.99935 6.66699M3.99935 12.0003C4.73524 12.0003 5.33268 11.4029 5.33268 10.667C5.33268 9.93111 4.73524 9.33366 3.99935 9.33366M3.99935 12.0003C3.26346 12.0003 2.66602 11.4029 2.66602 10.667C2.66602 9.93111 3.26346 9.33366 3.99935 9.33366M3.99935 12.0003V13.3337M3.99935 9.33366V2.66699M7.99935 6.66699V13.3337M11.9993 12.0003C12.7352 12.0003 13.3327 11.4029 13.3327 10.667C13.3327 9.93111 12.7352 9.33366 11.9993 9.33366M11.9993 12.0003C11.2635 12.0003 10.666 11.4029 10.666 10.667C10.666 9.93111 11.2635 9.33366 11.9993 9.33366M11.9993 12.0003V13.3337M11.9993 9.33366V2.66699"
                  stroke="white"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* <span>{t("Customize")}</span> */}
            </button>
            {showCustomRange && (
              <div className="subtitle-new-custom-range">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <label>
                    <span>{t("Start Date")}</span>
                    <DatePicker
                      value={customStartDate}
                      minDate={
                        customEndDate
                          ? subDays(customEndDate, maxCustomSpanDays)
                          : undefined
                      }
                      maxDate={customEndDate || undefined}
                      onChange={(newDate) => {
                        const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                        updateCustomRange("startDate", val);
                      }}
                      format={dateInputFormat}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: Boolean(dateError),
                          placeholder: userDateFormat,
                          sx: {
                            backgroundColor: "#fff",
                            width: "100%",
                            "& input": {
                              border: "none !important",
                              height: "auto !important",
                            },
                          },
                        },
                      }}
                    />
                  </label>
                  <label>
                    <span>{t("End Date")}</span>
                    <DatePicker
                      value={customEndDate}
                      minDate={customStartDate || undefined}
                      maxDate={
                        customStartDate
                          ? addDays(customStartDate, maxCustomSpanDays)
                          : undefined
                      }
                      onChange={(newDate) => {
                        const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                        updateCustomRange("endDate", val);
                      }}
                      format={dateInputFormat}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: Boolean(dateError),
                          placeholder: userDateFormat,
                          sx: {
                            backgroundColor: "#fff",
                            width: "100%",
                            "& input": {
                              border: "none !important",
                              height: "auto !important",
                            },
                          },
                        },
                      }}
                    />
                  </label>
                </LocalizationProvider>
                <Button
                  type="button"
                  variant="contained"
                  onClick={applyCustomRange}
                  disabled={Boolean(
                    customRange.startDate &&
                      customRange.endDate &&
                      getCustomRangeError(customRange.startDate, customRange.endDate),
                  )}
                >
                  {t("Apply")}
                </Button>
                <div className="subtitle-new-custom-range-note">
                  {t("Maximum range is 30 days")}
                </div>
                {(customRangeError || dateError) && (
                  <div className="subtitle-new-custom-range-error">
                    {t(customRangeError || dateError)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
